import React, { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { API_URL, SOCKET_URL } from "../../config/apiConfig";

/* ─── Map marker SVG data URIs ─────────────────────────────────────────────── */
const makeMarkerSVG = (color, emoji = "") =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
      <ellipse cx="20" cy="47" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
      <path d="M20 0C11.16 0 4 7.16 4 16c0 12 16 34 16 34S36 28 36 16C36 7.16 28.84 0 20 0z" fill="${color}"/>
      <circle cx="20" cy="16" r="10" fill="white" fill-opacity="0.9"/>
      <text x="20" y="21" text-anchor="middle" font-size="13">${emoji}</text>
    </svg>`
  )}`;

const CAR_ICON_URL     = makeMarkerSVG("#6366f1", "🚗");
const CAR_ACTIVE_URL   = makeMarkerSVG("#16a34a", "🚗");

/* ─── Smooth position interpolation ───────────────────────────────────────── */
const lerp = (a, b, t) => a + (b - a) * t;

function smoothMoveMarker(marker, targetLat, targetLng, steps = 25) {
  const startPos = marker.getPosition();
  const startLat = startPos.lat();
  const startLng = startPos.lng();
  let step = 0;

  const animate = () => {
    step++;
    const t = step / steps;
    const easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    marker.setPosition(
      new window.google.maps.LatLng(
        lerp(startLat, targetLat, easedT),
        lerp(startLng, targetLng, easedT)
      )
    );
    if (step < steps) requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
}

/* ─── Status badge helper ──────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    completed:   "bg-green-100 text-green-800",
    cancelled:   "bg-red-100 text-red-800",
    confirmed:   "bg-blue-100 text-blue-800",
    pending:     "bg-yellow-100 text-yellow-800",
    "in-progress": "bg-purple-100 text-purple-800",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status || "—"}
    </span>
  );
};

/* ─── Driver Detail Card ───────────────────────────────────────────────────── */
const DriverDetailCard = ({ ride, onClose }) => {
  if (!ride) return null;
  const driver = ride.driver || {};
  const user   = ride.user || {};
  const profilePic = driver.profilePhoto || driver.documents?.driverPhoto;

  return (
    <div
      className="absolute top-4 right-4 z-50 w-80 animate-slideIn max-h-[calc(100%-2rem)] flex flex-col"
      style={{ animation: "slideInRight 0.3s ease-out" }}
    >
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Card */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-full">

        {/* Header gradient band */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-5 py-4 relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-all cursor-pointer"
            title="Close"
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <div className="flex items-center gap-3">
            {profilePic ? (
              <img 
                src={profilePic} 
                alt={driver.name || "Driver"} 
                className="w-11 h-11 rounded-xl object-cover border border-white/20 shadow-inner"
              />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-black text-lg shadow-inner">
                {(driver.name || "D").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h3 className="text-white font-black text-sm tracking-tight leading-tight">
                {driver.name || "Unknown Driver"}
              </h3>
              <p className="text-indigo-200 text-xs font-semibold mt-0.5">
                {driver.phone || "No phone"} 
                {driver.vehicleNumber && <> • <span className="font-mono">{driver.vehicleNumber}</span></>}
              </p>
            </div>
          </div>

          {/* Live pulse indicator */}
          <div className="flex items-center gap-1.5 mt-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"/>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"/>
            </span>
            <span className="text-green-300 text-xs font-bold">LIVE TRACKING</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1 custom-scrollbar">

          {/* Booking reference */}
          {ride.bookingId && (
            <div className="flex items-center justify-between bg-indigo-50 rounded-xl px-3 py-2">
              <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Booking ID</span>
              <span className="text-xs font-black text-indigo-700 font-mono">{ride.bookingId}</span>
            </div>
          )}

          {/* Vehicle details */}
          <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Vehicle Info</p>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500 font-semibold">Model</span>
              <span className="text-xs font-bold text-slate-800">{driver.vehicleModel || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500 font-semibold">Plate</span>
              <span className="text-xs font-bold text-slate-800 font-mono">{driver.vehicleNumber || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500 font-semibold">Type</span>
              <span className="text-xs font-bold text-slate-800">{driver.vehicleType?.name || driver.vehicleType || "Standard"}</span>
            </div>
          </div>

          {/* Rider info */}
          {(user.name || ride.corporateMemberId) && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Passenger</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-black">
                  {(user.name || ride.corporateMemberId?.fullName || "P").charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800">{user.name || ride.corporateMemberId?.fullName || "—"}</p>
                  <p className="text-[10px] text-slate-400 font-semibold">{user.phone || ride.corporateMemberId?.mobileNumber || "—"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Route */}
          {(ride.pickupLocation || ride.dropLocation) && (
            <div className="space-y-2">
              {/* Pickup */}
              {ride.pickupLocation?.address && (
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 mt-0.5 rounded-full bg-green-500 flex-shrink-0 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-white"/>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">Pickup</p>
                    <p className="text-xs font-bold text-slate-700 leading-tight line-clamp-2">{ride.pickupLocation.address}</p>
                  </div>
                </div>
              )}

              {/* Dashed connector */}
              {ride.pickupLocation?.address && ride.dropLocation?.address && (
                <div className="ml-2 w-px h-3 bg-slate-300 border-l-2 border-dashed border-slate-300"/>
              )}

              {/* Drop */}
              {ride.dropLocation?.address && (
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 mt-0.5 rounded-full bg-red-500 flex-shrink-0 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-white"/>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">Drop</p>
                    <p className="text-xs font-bold text-slate-700 leading-tight line-clamp-2">{ride.dropLocation.address}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fare & Status */}
          <div className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-indigo-50 rounded-xl px-3 py-2.5">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">Fare</p>
              <p className="text-base font-black text-indigo-700">₹{(ride.totalAmount || 0).toLocaleString("en-IN")}</p>
            </div>
            <StatusBadge status={ride.status} />
          </div>

          {/* Driver status chips */}
          <div className="flex gap-2 flex-wrap">
            <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${driver.isAvailable ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>
              {driver.isAvailable ? "✓ Available" : "● On Ride"}
            </span>
            {driver.ratings > 0 && (
              <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-yellow-100 text-yellow-800">
                ★ {driver.ratings?.toFixed(1)} Rating
              </span>
            )}
            {driver.wallet?.balance !== undefined && (
              <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${driver.wallet.balance < 0 ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                ₹{(driver.wallet.balance || 0).toLocaleString("en-IN")} Wallet
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────────────────────── */
export default function AdminLiveTracking() {
  const mapRef      = useRef(null);
  const googleMap   = useRef(null);
  const markersRef  = useRef({});   // { [rideId]: { marker, ride } }
  const socketRef   = useRef(null);
  const mapLoaded   = useRef(false);

  const [socketStatus,    setSocketStatus]    = useState("connecting");
  const [lastUpdate,      setLastUpdate]      = useState(null);
  const [activeRidesCount,setActiveRidesCount]= useState(0);
  const [fetchedRides,    setFetchedRides]    = useState([]);
  const [selectedRideId,  setSelectedRideId]  = useState("all");
  const [selectedRide,    setSelectedRide]    = useState(null);   // for detail card

  /* ── Place a marker (or update existing) for a ride ── */
  const placeMarker = useCallback((rideId, lat, lng, ride) => {
    if (!googleMap.current) return;

    if (markersRef.current[rideId]) {
      smoothMoveMarker(markersRef.current[rideId].marker, lat, lng);
      markersRef.current[rideId].ride = ride || markersRef.current[rideId].ride;
    } else {
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: googleMap.current,
        title: ride?.driver?.name || `Ride ${rideId}`,
        icon: { url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) },
        zIndex: 999,
      });

      marker.addListener("click", async () => {
        const entry = markersRef.current[rideId];
        if (!entry) return;

        // Highlight clicked marker, reset all others
        Object.values(markersRef.current).forEach(e =>
          e.marker.setIcon({ url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) })
        );
        marker.setIcon({ url: CAR_ACTIVE_URL, scaledSize: new window.google.maps.Size(40, 50) });

        googleMap.current.panTo(marker.getPosition());

        if (!entry.ride) {
          try {
            const res = await fetch(`${API_URL}/booking/${rideId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            const data = await res.json();
            if (data.success && data.data) {
              entry.ride = data.data;
              setSelectedRide(data.data);
              setFetchedRides(prev => {
                if (!prev.some(r => r._id === rideId)) {
                  return [...prev, data.data];
                }
                return prev;
              });
            }
          } catch (err) {
            console.error("Failed to fetch ride details for", rideId, err);
          }
        } else {
          setSelectedRide(entry.ride);
        }
      });

      markersRef.current[rideId] = { marker, ride };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Init Google Map ── */
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google || mapLoaded.current) return;
    mapLoaded.current = true;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 5,
      center: { lat: 20.5937, lng: 78.9629 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        { featureType: "poi",     elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
      ],
    });

    // Click on empty map → deselect
    map.addListener("click", () => {
      setSelectedRide(null);
      Object.values(markersRef.current).forEach(e =>
        e.marker.setIcon({ url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) })
      );
    });

    googleMap.current = map;
  }, []);

  useEffect(() => {
    if (window.google?.maps?.Map) {
      initMap();
    } else {
      const iv = setInterval(() => {
        if (window.google?.maps?.Map) { clearInterval(iv); initMap(); }
      }, 500);
      return () => clearInterval(iv);
    }
  }, [initMap]);

  /* ── Fetch initial live rides ── */
  useEffect(() => {
    const fetchLiveRides = async () => {
      try {
        const res  = await fetch(`${API_URL}/booking/admin/live-rides`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        if (!data.success) return;

        setFetchedRides(data.data);
        data.data.forEach(ride => {
          const driver = ride.driver;
          if (driver?.currentLocation?.coordinates) {
            const [lng, lat] = driver.currentLocation.coordinates;
            placeMarker(ride._id, lat, lng, ride);
          }
        });
        setActiveRidesCount(data.data.length);
      } catch (err) {
        console.error("[AdminLiveTracking] fetch error:", err);
      }
    };

    const iv = setInterval(() => {
      if (googleMap.current) { clearInterval(iv); fetchLiveRides(); }
    }, 500);
    return () => clearInterval(iv);
  }, [placeMarker]);

  /* ── Focus on selected ride from dropdown ── */
  useEffect(() => {
    if (selectedRideId === "all") {
      const entries = Object.values(markersRef.current);
      if (googleMap.current && entries.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        entries.forEach(e => bounds.extend(e.marker.getPosition()));
        googleMap.current.fitBounds(bounds);
      }
    } else {
      const entry = markersRef.current[selectedRideId];
      if (entry && googleMap.current) {
        googleMap.current.setCenter(entry.marker.getPosition());
        googleMap.current.setZoom(15);
        setSelectedRide(entry.ride || null);
        Object.values(markersRef.current).forEach(e =>
          e.marker.setIcon({ url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) })
        );
        entry.marker.setIcon({ url: CAR_ACTIVE_URL, scaledSize: new window.google.maps.Size(40, 50) });
      }
    }
  }, [selectedRideId]);

  /* ── Socket real-time updates ── */
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["polling", "websocket"],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 30000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketStatus("live");
      socket.emit("joinAdminRoom");
    });

    socket.on("locationUpdate", ({ rideId, lat, lng, type }) => {
      const parsedLat = parseFloat(lat);
      const parsedLng = parseFloat(lng);
      setLastUpdate(new Date());

      if (!type || type === "driver") {
        placeMarker(rideId, parsedLat, parsedLng, null);
        setActiveRidesCount(Object.keys(markersRef.current).length);
      }
    });

    socket.on("disconnect", () => setSocketStatus("disconnected"));

    return () => {
      socket.disconnect();
      Object.values(markersRef.current).forEach(e => e.marker.setMap(null));
    };
  }, [placeMarker]);

  /* ── Render ── */
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Top bar */}
      <div
        style={{ flexShrink: 0 }}
        className="flex justify-between items-center px-5 py-3 bg-white border-b border-gray-100 shadow-sm flex-wrap gap-2"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-800 leading-tight">Live Driver Tracking</h1>
          <p className="text-gray-400 text-xs">Click any driver marker to view full details</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Socket status */}
          <div className="bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              {socketStatus === "live" && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"/>
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${socketStatus === "live" ? "bg-green-500" : "bg-red-500"}`}/>
            </span>
            <span className="text-xs font-semibold text-gray-700">
              {socketStatus === "live" ? "Connected" : "Disconnected"}
            </span>
          </div>

          {/* Focus dropdown */}
          <div className="bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 flex items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold">Focus:</span>
            <select
              className="text-xs font-bold border-0 focus:ring-0 cursor-pointer bg-transparent text-gray-800"
              value={selectedRideId}
              onChange={e => setSelectedRideId(e.target.value)}
            >
              <option value="all">All Drivers</option>
              {fetchedRides.map(ride =>
                ride.driver && (
                  <option key={ride._id} value={ride._id}>
                    {ride.driver.name} ({ride.bookingId || "No ID"})
                  </option>
                )
              )}
            </select>
          </div>

          {/* Active rides count */}
          <div className="bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 flex items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold">Active Rides:</span>
            <span className="text-base font-black text-indigo-600">{activeRidesCount}</span>
          </div>

          {/* Last update */}
          {lastUpdate && (
            <div className="bg-gray-50 px-3 py-2 rounded-xl border border-gray-100">
              <span className="text-xs text-gray-400 font-semibold">
                {lastUpdate.toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Map container – fills all remaining height */}
      <div
        className="relative overflow-hidden"
        style={{ flex: "1 1 0", minHeight: 0 }}
      >
        {/* Map */}
        <div ref={mapRef} style={{ width: "100%", height: "100%", minHeight: "450px", borderRadius: "1rem" }} />

        {/* Hint overlay when no driver selected */}
        {!selectedRide && activeRidesCount > 0 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full pointer-events-none">
            🚗 Click on a driver marker to see details
          </div>
        )}

        {/* No drivers state */}
        {activeRidesCount === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-2xl">
            <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
              <span className="text-3xl">🚗</span>
            </div>
            <h3 className="text-lg font-black text-slate-700">No Active Drivers</h3>
            <p className="text-sm text-slate-400 font-semibold mt-1">Waiting for drivers to come online…</p>
          </div>
        )}

        {/* Driver Detail Card – floating over map */}
        {selectedRide && (
          <DriverDetailCard
            ride={selectedRide}
            onClose={() => {
              setSelectedRide(null);
              Object.values(markersRef.current).forEach(e =>
                e.marker.setIcon({ url: CAR_ICON_URL, scaledSize: new window.google.maps.Size(40, 50) })
              );
            }}
          />
        )}
      </div>
    </div>
  );
}
